"use client";

import { ArrowRight, FileText } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactElement } from "react";
import { useHelpHref } from "./help-link-base";
import type { HelpTreeNode } from "./help-tree";

/** Pages listed inside a card before "Alle n Seiten". */
const CARD_PREVIEW_COUNT = 4;

/** Level-2 page with children: title, count and its first subpages. Page descriptions stay hidden. */
export function HelpSpaceSectionCard({
  org_slug,
  node,
}: {
  readonly org_slug: string;
  readonly node: HelpTreeNode;
}): ReactElement {
  const t = useTranslations("public.helpSpaceOverview");
  const helpHref = useHelpHref(org_slug);
  const pageHref = helpHref({
    kind: "page",
    pageId: node.page.page_id,
  });
  const shown = node.children.slice(0, CARD_PREVIEW_COUNT);
  const hasMore = node.children.length > shown.length;

  return (
    <article className="flex flex-col rounded-xl border border-border/70 bg-background p-5 transition-colors hover:border-border">
      <Link
        href={pageHref}
        prefetch={false}
        className="group/title block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-action-500/30"
      >
        <h2 className="text-[15.5px] font-semibold leading-snug text-primary/90 group-hover/title:text-primary">
          {node.page.title}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-primary/55">
          <span className="font-medium text-action-700">
            {t("seiten", { count: node.children.length })}
          </span>
        </p>
      </Link>
      <ul className="mt-4 space-y-0.5 border-t border-border/60 pt-3">
        {shown.map((child) => (
          <li key={child.page.page_id}>
            <Link
              href={helpHref({
                kind: "page",
                pageId: child.page.page_id,
              })}
              prefetch={false}
              className="group/item -mx-2 flex items-start gap-2.5 rounded-md px-2 py-1.5 text-[13.5px] leading-5 text-primary/70 outline-none transition-colors hover:bg-muted/60 hover:text-primary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500/30"
            >
              <FileText
                aria-hidden
                strokeWidth={1.75}
                className="mt-[3px] h-3.5 w-3.5 shrink-0 text-primary/30 group-hover/item:text-primary/50"
              />
              <span className="min-w-0 flex-1">{child.page.title}</span>
              {child.children.length > 0 ? (
                <span className="shrink-0 text-[12px] text-primary/40">
                  {t("seiten", { count: child.children.length })}
                </span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
      {hasMore ? (
        <Link
          href={pageHref}
          prefetch={false}
          className="mt-auto inline-flex items-center gap-1 self-start pt-3 text-[13px] font-medium text-action-700 hover:text-action-900"
        >
          {t("alleSeiten", { count: node.children.length })}
          <ArrowRight aria-hidden className="h-3.5 w-3.5" />
        </Link>
      ) : null}
    </article>
  );
}
